import {gql} from '@apollo/client';

export const GET_ALL_REWARD_SCHEMES = gql`
  query GetAllRewardSchemes(
    $page: Int
    $limit: Int
    $type: RewardSchemeType
    $isActive: Boolean
  ) {
    getAllRewardSchemes(
      page: $page
      limit: $limit
      type: $type
      isActive: $isActive
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAllRewardSchemes {
        pagination {
          page
          limit
          pages
          total
          hasPrevPage
          hasNextPage
          previous
          next
        }
        message
        data {
          stats {
            totalDepositGeneratedFromRewards {
              totalAmount
              currency
            }
            totalActiveRewardPrograms
            totalNewUsersFromRewards
          }
          rewardSchemes {
            id
            title
            type
            rewardConfig {
              instruments {
                instrumentId
                ticker
                exchangeCode
                instrumentKey
                rewardUnits
              }
              cash {
                amount
                currency {
                  currencySymbol
                  currency
                  currencyName
                }
              }
              transactionFeeWaiver {
                percentage
              }
            }
            isActive
            isExpired
          }
        }
      }
    }
  }
`;

export const GET_REWARD_SCHEME = gql`
  query GetRewardScheme($getRewardSchemeId: ID!, $language: Languages!) {
    getRewardScheme(id: $getRewardSchemeId, language: $language) {
      ... on Error {
        message
        statusCode
      }
      ... on TypeRewardScheme {
        id
        title
        description
        disclaimer
        type
        rewardType {
          referral {
            referee
            referrer
          }
          transactional
        }
        maxNumberOfRewards
        operationConfig {
          type
          rewardTriggerOccurrence
          assetClass
          instrumentKeys
          cumulativeDurationUnit
          cumulativeDurationValue
          instructions {
            titleText
            descriptionText
            icon
          }
          allowedCurrencies {
            currency {
              currencySymbol
              currency
              currencyName
            }
            minTransactionValue
          }
          wheelSpinConfig {
            maxNumberOfSpins
            expirationDurationValue
            expirationDurationUnit
            segments {
              reward
              weight
            }
          }
          minNumberOfTransactions
          transactionStatuses
          requireDabaPro
          minPortfolioInvestedAmountInXOF
          rewardExpirationDurationValue
          rewardExpirationDurationUnit
          durationDescription
          winnersAnnouncedTimeframe
          prizeDistributionTimeframe
          leaderboardConfig {
            isRecurring
            rankingMethod
            tieBreaker
          }
        }
        isActive
        isExpired
        expirationDate
        startDate
        allowConcurrency
        rewardConfig {
          instruments {
            instrumentId
            ticker
            exchangeCode
            instrumentKey
            rewardUnits
          }
          cash {
            currency {
              currencySymbol
              currency
              currencyName
            }
            amount
          }
          transactionFeeWaiver {
            maxFeeWaiverConfig {
              currency {
                currency
                currencySymbol
                currencyName
              }
              value
            }
            percentage
          }
          rewardTiers {
            position
            reward {
              rewardType
              cash {
                currency {
                  currency
                }
                amount
              }
              instruments {
                instrumentId
                rewardUnits
                ticker
                exchangeCode
                instrumentKey
              }
              transactionFeeWaiver {
                percentage
                maxFeeWaiverConfig {
                  currency {
                    currency
                  }
                  value
                }
              }
            }
          }
        }
        createdAt
        updatedAt
        language
      }
    }
  }
`;

export const GET_USER_REWARDS = gql`
  query AdminGetAllUserRewards(
    $userType: [UserRewardUserType]
    $entryMode: EntryMode
    $status: RewardStatus
    $statuses: [RewardStatus]
    $page: Int
    $limit: Int
    $user: ID
    $rewardSchemeId: ID
    $rewardSchemeIds: [ID]
    $rewardType: RewardType
    $eventType: RewardProgramOperationType
    $currency: String
    $mode: AppMode
    $startDate: DateTime
    $endDate: DateTime
  ) {
    adminGetAllUserRewards(
      userType: $userType
      entryMode: $entryMode
      status: $status
      statuses: $statuses
      page: $page
      limit: $limit
      user: $user
      rewardSchemeId: $rewardSchemeId
      rewardSchemeIds: $rewardSchemeIds
      rewardType: $rewardType
      eventType: $eventType
      currency: $currency
      mode: $mode
      startDate: $startDate
      endDate: $endDate
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAllUserRewards {
        data {
          id
          status
          createdAt
          updatedAt
          entryMode
          walletTransactionId
          reversalRecord {
            status
            reversalReason
          }
          eventAmount
          eventFees
          eventAssetClassKey
          eventInstrumentKey
          rewardScheme {
            id
            title
            type
          }
          eventCurrency {
            currency
            currencySymbol
            currencyName
          }
          eventType
          userId {
            firstName
            id
            lastName
            imageUrl
          }
          refereeId {
            id
            firstName
            lastName
          }
          referrerId {
            firstName
            id
            lastName
          }
          userType
          userReward {
            type
            ticker
            exchangeCode
            stockPrice
            rewardUnits
            cashValue
            amount
            currency {
              currency
            }
          }
        }
        message
        pagination {
          page
          limit
          pages
          total
          hasPrevPage
          hasNextPage
          previous
          next
        }
      }
    }
  }
`;

export const GET_ALL_STOCK_REWARDS = gql`
  query GetAllStockBanks($page: Int, $limit: Int) {
    getAllStockBanks(page: $page, limit: $limit) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAllStockRewardBank {
        message
        data {
          stockRewardBank {
            id
            purchasedUnits
            ticker
            disbursedUnits
            instrumentId
            instrumentKey
            exchangeCode
          }
          stockStats {
            totalStocksUnitsLeft
            totalStocksUnitIssued
            totalNewUsersFromStockReward
          }
        }
        pagination {
          page
          limit
          pages
          total
          hasPrevPage
          hasNextPage
          previous
          next
        }
      }
    }
  }
`;

export const GET_ALL_CASH_BANKS = gql`
  query GetAllCashBanks($page: Int, $limit: Int) {
    getAllCashBanks(page: $page, limit: $limit) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAllCashRewardBank {
        message
        data {
          cashRewardBank {
            id
            amountDisbursed
            amountDeposited
            currency
          }
          cashStats {
            totalNewUsersFromRewards
          }
        }
        pagination {
          page
          limit
          pages
          total
          hasPrevPage
          hasNextPage
          previous
          next
        }
      }
    }
  }
`;

export const GET_ALL_STOCKS_BY_STOCK_EXCHANGE = gql`
  query GetAllStocksByStockExchange($stockExchangeCode: String!) {
    getAllStocksByStockExchange(stockExchangeCode: $stockExchangeCode) {
      ... on Error {
        message
        statusCode
      }
      ... on CompanyStockType {
        stocks {
          company {
            name
            logoImgURL
            StockExchange {
              id
            }
            id
          }
          symbol
        }
      }
    }
  }
`;

/**
 * Public equities available to back a stock reward bank.
 *
 * Replaces `GET_ALL_STOCKS_BY_STOCK_EXCHANGE`, which read the pre-v2
 * company/stock world and now returns nothing at all — the create form's
 * ticker dropdown was empty. A reward bank is created against an
 * `instrumentId`; supplying `ticker`, `companyId` or `stockExchangeId`
 * returns a migration error, so the picker has to offer instruments.
 *
 * `exchangeCode` is carried because a ticker is no longer unique — the same
 * symbol can list on two exchanges.
 */
export const GET_PUBLIC_EQUITY_INSTRUMENTS = gql`
  query GetPublicEquityInstruments($input: FinancialInstrumentFilter!) {
    getFinancialInstruments(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithFinancialInstruments {
        data {
          ... on PublicEquity {
            id
            name
            ticker
            exchange {
              code
            }
          }
        }
      }
    }
  }
`;

export const GET_REWARD_LANGUAGE_VERSION = gql`
  query GetRewardsLanguageVersions($getRewardsLanguageVersionsId: ID!) {
    getRewardsLanguageVersions(id: $getRewardsLanguageVersionsId) {
      ... on Error {
        message
        statusCode
      }
      ... on RewardsLanguageVersion {
        languages
        title
        rewardScheme
      }
    }
  }
`;

export const GET_USER_REWARD_BY_ID = gql`
  query GetUserRewardById($getUserRewardByIdId: ID!) {
    getUserRewardById(id: $getUserRewardByIdId) {
      ... on Error {
        statusCode
        message
      }
      ... on UserReward {
        referrerId {
          email
          id
          firstName
          lastName
        }
        refereeId {
          email
          id
          firstName
          lastName
        }
        userId {
          email
          id
          firstName
          lastName
        }
        rewardScheme {
          id
        }
        userReward {
          currency {
            currency
            currencyName
          }
          type
          ticker
          exchangeCode
          instrumentId
          rewardUnits
          amount
        }
        eventType
        eventId
        eventAmount
        eventFees
        eventAssetClass
        eventAssetClassKey
        eventInstrumentKey
        eventCurrency {
          currency
          currencyName
        }
        status
        userType
        id
        createdAt
        entryMode
      }
    }
  }
`;
